import React from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ArrowRight,
  Sparkles,
  Building2,
  Hammer,
  ClipboardList,
  Home as HomeIcon,
  Phone,
  Stethoscope,
  ShoppingBag,
  GraduationCap,
  Waves,
  Warehouse,
  Landmark,
} from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";

const COMMERCIAL_FACILITIES = [
  { icon: Building2, label: "Commercial Offices" },
  { icon: Building2, label: "Corporate Facilities" },
  { icon: Hammer, label: "Builders' Final Cleans" },
  { icon: Building2, label: "Commercial Developments" },
  { icon: Stethoscope, label: "Medical Centres" },
  { icon: Stethoscope, label: "Hospitals" },
  { icon: GraduationCap, label: "Childcare Centres" },
  { icon: GraduationCap, label: "Schools & Educational" },
  { icon: Waves, label: "Leisure & Aquatic Centres" },
  { icon: ShoppingBag, label: "Retail & Shopping Centres" },
  { icon: Warehouse, label: "Industrial & Warehouse" },
  { icon: Building2, label: "Body Corporate & Common Areas" },
  { icon: Landmark, label: "Government Buildings" },
];

const SERVICES = [
  {
    id: "commercial",
    icon: Building2,
    title: "Commercial Cleaning",
    subtitle: "Our core focus. Contracts built for commercial environments.",
    featured: true,
    what: "Professional commercial cleaning contracts and one-off projects for businesses, corporate facilities, developments and specialised environments across South East Queensland.",
    who: "Business owners, office managers, healthcare and education providers, property managers, body corporate committees, government departments, and commercial property owners.",
    includes: [
      "Recurring commercial contracts (daily, weekday, weekly or out-of-hours)",
      "One-off deep cleans, spring cleans and pre-event cleans",
      "Kitchen, bathroom and touchpoint sanitisation",
      "Floor care: vacuum, mop, and hard-floor maintenance",
      "Glass, meeting room and reception presentation",
      "Consumables monitoring and restocking (as agreed)",
      "Written scope and quality checklist for every site",
    ],
    why: "Commercial cleaning isn't just one of our services — it's what we do best. Our systems, equipment and quality assurance are built specifically to meet the demands of commercial environments.",
    isCommercialGrid: true,
  },
  {
    id: "builders",
    icon: Hammer,
    title: "Builders' Final Cleans",
    subtitle: "Handover-ready presentation, delivered on schedule.",
    what: "Preparing newly completed homes, apartment complexes and commercial developments for handover is one of our core specialities. We deliver a finish that presents your project to the highest possible standard.",
    who: "Builders, developers, project managers and site supervisors preparing properties for settlement, client walkthroughs, first occupancy or property styling.",
    includes: [
      "Full internal detailing after trades finish — no shortcuts",
      "Removal of building dust, plaster residue, paint splatter, tape and stickers",
      "Windows, tracks, sills and flyscreens cleaned",
      "Fixtures, tapware, ovens and appliances polished",
      "Floors vacuumed, mopped, hard-floor sealed as required",
      "Cabinetry, drawers and pantries wiped inside and out",
      "Balconies, garages and common areas swept-down as agreed",
    ],
    why: "We understand builders' timelines. We turn up when we say we will, coordinate around remaining trades where needed, and hand back a site that's genuinely ready for the client — not one that needs a second clean.",
  },
  {
    id: "bond",
    icon: ClipboardList,
    title: "Bond & End of Lease Cleaning",
    subtitle: "Comprehensive, inspection-ready exit cleans.",
    what: "A comprehensive top-to-bottom clean designed to help prepare properties for final inspection, maximise the likelihood of bond return, and ensure every property is left fresh, spotless and ready for its next occupants.",
    who: "Tenants moving out, landlords preparing for the next tenancy, real-estate agencies and property managers coordinating handovers on behalf of clients.",
    includes: [
      "Full interior clean: every room, cupboard and drawer",
      "Ovens, rangehoods, cooktops, dishwashers — degreased and detailed",
      "Bathrooms deep-cleaned: grout, tracks, exhaust fans, tiles",
      "Windows (internal), tracks, sills, flyscreens dusted",
      "Skirtings, door frames, light switches and vents wiped",
      "Wardrobes, cupboards, drawers cleaned inside and out",
      "Balcony and garage sweep-down (where accessible)",
    ],
    why: "Bond cleans are where corner-cutting shows up the fastest — and costs the most. We work to a written scope, walk the property before we leave, and remain contactable if the property manager flags anything post-inspection.",
  },
  {
    id: "residential",
    icon: HomeIcon,
    title: "Residential Cleaning",
    subtitle: "Selected residential work, same commercial standard.",
    what: "While commercial cleaning is our primary focus, we also provide selected residential cleaning services for homeowners who value professional workmanship, reliability and exceptional attention to detail.",
    who: "Busy families, professionals, downsizers and long-term homeowners who want a cleaner they can genuinely rely on.",
    includes: [
      "Regular weekly, fortnightly or monthly maintenance cleans",
      "One-off deep cleans and spring cleans",
      "Kitchens, bathrooms and living areas detailed",
      "Dust removal on skirtings, sills, blinds and fittings",
      "Floors vacuumed and mopped throughout",
      "Bins emptied, surfaces tidied, beds made on request",
    ],
    why: "You'll always get the same careful team, structured the same way as our commercial contracts — the same booking, the same communication, the same delivery standard.",
    isCommercialGrid: false,
  },
];

export default function Services() {
  return (
    <>
      <SEO
        title="Commercial Cleaning Services | Savage Cleaning — Brisbane, Gold Coast & SEQ"
        description="Commercial cleaning, builders' final cleans, bond cleans and residential cleaning across Brisbane, the Gold Coast and South East Queensland. Request a free, tailored quote."
      />

      {/* HERO */}
      <section className="relative py-24 sm:py-32 border-b border-white/5" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="gold-rule" />
              <span className="eyebrow">Services</span>
            </div>
            <h1 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]">
              Commercial-led cleaning solutions, delivered without compromise.
            </h1>
            <p className="mt-8 text-lg text-white/80 leading-relaxed max-w-2xl">
              Commercial contracts, builders' final cleans, bond cleans and selected residential
              work. Every project is scoped in writing, priced clearly and delivered by a team
              built for commercial-grade standards.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-primary" data-testid="services-hero-cta">
                Get a Free Quote <ArrowRight size={16} />
              </Link>
              <a href="tel:0416238430" className="btn-outline" data-testid="services-hero-call">
                <Phone size={16} /> 0416 238 430
              </a>
            </div>
          </div>

          {/* Quick jump */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SERVICES.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="border border-white/10 hover:border-[#C9A24B]/60 px-4 py-3 text-sm text-white/85 hover:text-[#C9A24B] transition-colors flex items-center gap-2"
                data-testid={`services-jump-${s.id}`}
              >
                <s.icon size={16} className="text-[#C9A24B]" strokeWidth={1.5} />
                {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE SECTIONS */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32 space-y-24 sm:space-y-32">
        {SERVICES.map((s, idx) => (
          <section
            key={s.id}
            id={s.id}
            className="scroll-mt-24"
            data-testid={`service-section-${s.id}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-5">
                <div className="eyebrow">Service 0{idx + 1}</div>
                <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl text-white leading-tight">
                  {s.title}
                </h2>
                <p className="mt-4 text-[#C9A24B] font-serif-display text-xl italic">
                  {s.subtitle}
                </p>
                <div className="mt-8 space-y-6 text-white/80 leading-relaxed">
                  <div>
                    <div className="eyebrow mb-2">What it is</div>
                    <p>{s.what}</p>
                  </div>
                  <div>
                    <div className="eyebrow mb-2">Who it's for</div>
                    <p>{s.who}</p>
                  </div>
                  <div>
                    <div className="eyebrow mb-2">Why Savage Cleaning</div>
                    <p>{s.why}</p>
                  </div>
                </div>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to="/contact" className="btn-primary" data-testid={`service-cta-${s.id}`}>
                    Enquire about {s.title.replace(" Cleaning", "")} <ArrowRight size={16} />
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className={s.featured ? "card-bond" : "card-dark"}>
                  {s.featured && (
                    <div className="flex items-center gap-3 mb-6">
                      <Sparkles size={18} className="text-[#C9A24B]" />
                      <span className="eyebrow">Our core focus</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <s.icon size={26} className="text-[#C9A24B]" strokeWidth={1.4} />
                    <h3 className="font-serif-display text-2xl sm:text-3xl text-white">
                      {s.featured ? "We specialise in cleaning" : "What's included"}
                    </h3>
                  </div>

                  {s.isCommercialGrid && (
                    <>
                      <p className="text-white/75 mb-6 leading-relaxed">
                        Professional cleaning solutions for:
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
                        {COMMERCIAL_FACILITIES.map(({ icon: Icon, label }) => (
                          <div key={label} className="flex items-center gap-3 text-white/85">
                            <Icon size={16} className="text-[#C9A24B] flex-shrink-0" strokeWidth={1.6} />
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                      <div className="pt-6 border-t border-white/10">
                        <div className="flex items-center gap-3 mb-4">
                          <Check size={18} className="text-[#C9A24B]" strokeWidth={2} />
                          <span className="eyebrow">Our commercial cleaning includes</span>
                        </div>
                        <ul className="space-y-3">
                          {s.includes.map((line) => (
                            <li key={line} className="flex gap-3 text-white/85 leading-relaxed">
                              <Check size={18} className="text-[#C9A24B] mt-1 flex-shrink-0" strokeWidth={1.8} />
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}

                  {!s.isCommercialGrid && (
                    <ul className="space-y-3 mt-2">
                      {s.includes.map((line) => (
                        <li key={line} className="flex gap-3 text-white/85 leading-relaxed">
                          <Check size={18} className="text-[#C9A24B] mt-1 flex-shrink-0" strokeWidth={1.8} />
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      <CTASection
        title="Need a commercial cleaning partner you can rely on?"
        subtitle="Tell us about your project, contract or site and we'll come back with a clear scope and honest quote."
      />
    </>
  );
}
